// src/pages/Admin/Settings/AdminSettings.tsx
import React, { useState } from 'react';
import {
  Settings,
  Mail,
  Shield,
  Database,
  Globe,
  Bell,
  Save,
  Check,
  Eye,
  EyeOff
} from 'lucide-react';
import { useToast } from '../../../hooks/useToast';

interface SettingsSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
}

interface GeneralSettings {
  platform_name: string;
  platform_description: string;
  contact_email: string;
  support_email: string;
  maintenance_mode: boolean;
  user_registration_enabled: boolean;
  content_approval_required: boolean;
  max_upload_size_mb: number;
}

interface SecuritySettings {
  jwt_secret: string;
  password_min_length: number;
  require_email_verification: boolean;
  enable_two_factor_auth: boolean;
  session_timeout_minutes: number;
  max_login_attempts: number;
  account_lockout_duration_minutes: number;
}

interface EmailSettings {
  smtp_host: string;
  smtp_port: number;
  smtp_username: string;
  smtp_password: string;
  smtp_use_tls: boolean;
  from_email: string;
  from_name: string;
  email_verification_enabled: boolean;
  password_reset_enabled: boolean;
}

const AdminSettings: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [activeSection, setActiveSection] = useState('general');
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);

  const [generalSettings, setGeneralSettings] = useState<GeneralSettings>({
    platform_name: 'StrymHub',
    platform_description: 'Your premier destination for entertainment content',
    contact_email: 'contact@strymhub.com',
    support_email: 'support@strymhub.com',
    maintenance_mode: false,
    user_registration_enabled: true,
    content_approval_required: false,
    max_upload_size_mb: 100
  });

  const [securitySettings, setSecuritySettings] = useState<SecuritySettings>({
    jwt_secret: '••••••••••••••••',
    password_min_length: 8,
    require_email_verification: true,
    enable_two_factor_auth: false,
    session_timeout_minutes: 60,
    max_login_attempts: 5,
    account_lockout_duration_minutes: 15
  });

  const [emailSettings, setEmailSettings] = useState<EmailSettings>({
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_username: 'noreply@strymhub.com',
    smtp_password: '••••••••••••••••',
    smtp_use_tls: true,
    from_email: 'noreply@strymhub.com',
    from_name: 'StrymHub Platform',
    email_verification_enabled: true,
    password_reset_enabled: true
  });

  const sections: SettingsSection[] = [
    {
      id: 'general',
      title: 'General Settings',
      icon: <Settings className="h-5 w-5" />,
      description: 'Basic platform configuration and preferences'
    },
    {
      id: 'security',
      title: 'Security',
      icon: <Shield className="h-5 w-5" />,
      description: 'Authentication and security configurations'
    },
    {
      id: 'email',
      title: 'Email Configuration',
      icon: <Mail className="h-5 w-5" />,
      description: 'SMTP settings and email templates'
    },
    {
      id: 'database',
      title: 'Database',
      icon: <Database className="h-5 w-5" />,
      description: 'Database maintenance and backup settings'
    },
    {
      id: 'api',
      title: 'API Settings',
      icon: <Globe className="h-5 w-5" />,
      description: 'API keys and external service integrations'
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: <Bell className="h-5 w-5" />,
      description: 'System notifications and alerts configuration'
    }
  ];

  const handleSaveSettings = async (section: string) => {
    setSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      showSuccess(`${section.charAt(0).toUpperCase() + section.slice(1)} settings saved successfully`);
    } catch (_error) {
      showError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Platform Information</h3>
        <div className="grid grid-cols-1 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Platform Name
            </label>
            <input
              type="text"
              value={generalSettings.platform_name}
              onChange={(e) => setGeneralSettings(prev => ({ ...prev, platform_name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Platform Description
            </label>
            <textarea
              value={generalSettings.platform_description}
              onChange={(e) => setGeneralSettings(prev => ({ ...prev, platform_description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Email
              </label>
              <input
                type="email"
                value={generalSettings.contact_email}
                onChange={(e) => setGeneralSettings(prev => ({ ...prev, contact_email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Support Email
              </label>
              <input
                type="email"
                value={generalSettings.support_email}
                onChange={(e) => setGeneralSettings(prev => ({ ...prev, support_email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Platform Controls</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Maintenance Mode</label>
              <p className="text-sm text-gray-600">Temporarily disable public access to the platform</p>
            </div>
            <button
              onClick={() => setGeneralSettings(prev => ({ ...prev, maintenance_mode: !prev.maintenance_mode }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                generalSettings.maintenance_mode ? 'bg-red-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  generalSettings.maintenance_mode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">User Registration</label>
              <p className="text-sm text-gray-600">Allow new users to register accounts</p>
            </div>
            <button
              onClick={() => setGeneralSettings(prev => ({ ...prev, user_registration_enabled: !prev.user_registration_enabled }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                generalSettings.user_registration_enabled ? 'bg-green-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  generalSettings.user_registration_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Content Approval Required</label>
              <p className="text-sm text-gray-600">Require admin approval before content is published</p>
            </div>
            <button
              onClick={() => setGeneralSettings(prev => ({ ...prev, content_approval_required: !prev.content_approval_required }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                generalSettings.content_approval_required ? 'bg-orange-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  generalSettings.content_approval_required ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Maximum Upload Size (MB)
            </label>
            <input
              type="number"
              min="1"
              max="1000"
              value={generalSettings.max_upload_size_mb}
              onChange={(e) => setGeneralSettings(prev => ({ ...prev, max_upload_size_mb: parseInt(e.target.value) || 100 }))}
              className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Authentication</h3>
        <div className="grid grid-cols-1 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              JWT Secret Key
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={securitySettings.jwt_secret}
                onChange={(e) => setSecuritySettings(prev => ({ ...prev, jwt_secret: e.target.value }))}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Password Length
            </label>
            <input
              type="number"
              min="6"
              max="32"
              value={securitySettings.password_min_length}
              onChange={(e) => setSecuritySettings(prev => ({ ...prev, password_min_length: parseInt(e.target.value) || 8 }))}
              className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session Timeout (minutes)
              </label>
              <input
                type="number"
                min="15"
                max="1440"
                value={securitySettings.session_timeout_minutes}
                onChange={(e) => setSecuritySettings(prev => ({ ...prev, session_timeout_minutes: parseInt(e.target.value) || 60 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Login Attempts
              </label>
              <input
                type="number"
                min="3"
                max="10"
                value={securitySettings.max_login_attempts}
                onChange={(e) => setSecuritySettings(prev => ({ ...prev, max_login_attempts: parseInt(e.target.value) || 5 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Security Features</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Require Email Verification</label>
              <p className="text-sm text-gray-600">Users must verify their email before accessing the platform</p>
            </div>
            <button
              onClick={() => setSecuritySettings(prev => ({ ...prev, require_email_verification: !prev.require_email_verification }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                securitySettings.require_email_verification ? 'bg-green-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  securitySettings.require_email_verification ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Two-Factor Authentication</label>
              <p className="text-sm text-gray-600">Enable 2FA for admin accounts</p>
            </div>
            <button
              onClick={() => setSecuritySettings(prev => ({ ...prev, enable_two_factor_auth: !prev.enable_two_factor_auth }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                securitySettings.enable_two_factor_auth ? 'bg-orange-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  securitySettings.enable_two_factor_auth ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEmailSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">SMTP Configuration</h3>
        <div className="grid grid-cols-1 gap-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Host
              </label>
              <input
                type="text"
                value={emailSettings.smtp_host}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="smtp.gmail.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Port
              </label>
              <input
                type="number"
                value={emailSettings.smtp_port}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 587 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Username
            </label>
            <input
              type="text"
              value={emailSettings.smtp_username}
              onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_username: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showSmtpPassword ? 'text' : 'password'}
                value={emailSettings.smtp_password}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showSmtpPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Use TLS</label>
              <p className="text-sm text-gray-600">Enable TLS encryption for SMTP</p>
            </div>
            <button
              onClick={() => setEmailSettings(prev => ({ ...prev, smtp_use_tls: !prev.smtp_use_tls }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                emailSettings.smtp_use_tls ? 'bg-green-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  emailSettings.smtp_use_tls ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Email Templates</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              From Email
            </label>
            <input
              type="email"
              value={emailSettings.from_email}
              onChange={(e) => setEmailSettings(prev => ({ ...prev, from_email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              From Name
            </label>
            <input
              type="text"
              value={emailSettings.from_name}
              onChange={(e) => setEmailSettings(prev => ({ ...prev, from_name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Email Verification</label>
              <p className="text-sm text-gray-600">Send verification emails to new users</p>
            </div>
            <button
              onClick={() => setEmailSettings(prev => ({ ...prev, email_verification_enabled: !prev.email_verification_enabled }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                emailSettings.email_verification_enabled ? 'bg-green-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  emailSettings.email_verification_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Password Reset</label>
              <p className="text-sm text-gray-600">Enable password reset via email</p>
            </div>
            <button
              onClick={() => setEmailSettings(prev => ({ ...prev, password_reset_enabled: !prev.password_reset_enabled }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                emailSettings.password_reset_enabled ? 'bg-green-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  emailSettings.password_reset_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPlaceholder = (title: string) => (
    <div className="text-center py-12">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Settings className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 max-w-md mx-auto">
        This settings section is coming soon. Configure your platform's {title.toLowerCase()} settings here.
      </p>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'general':
        return renderGeneralSettings();
      case 'security':
        return renderSecuritySettings();
      case 'email':
        return renderEmailSettings();
      case 'database':
        return renderPlaceholder('Database Settings');
      case 'api':
        return renderPlaceholder('API Settings');
      case 'notifications':
        return renderPlaceholder('Notification Settings');
      default:
        return renderGeneralSettings();
    }
  };

  return (
    <div className="flex flex-col lg:flex-row h-full">
      {/* Sidebar */}
      <div className="w-full lg:w-80 bg-white border-b lg:border-b-0 lg:border-r border-gray-200 flex-shrink-0">
        <div className="p-3 sm:p-4 lg:p-6">
          <div className="flex items-center space-x-3 mb-4 sm:mb-6">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Settings className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-900">Admin Settings</h1>
              <p className="text-xs sm:text-sm text-gray-600">Platform configuration</p>
            </div>
          </div>

          <nav className="space-y-1 lg:space-y-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-1 lg:gap-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-start space-x-2 sm:space-x-3 px-2 sm:px-3 py-2 sm:py-3 text-left rounded-lg transition-colors ${
                    activeSection === section.id
                      ? 'bg-orange-50 text-orange-700 border-l-2 sm:border-l-4 border-orange-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {React.cloneElement(section.icon as React.ReactElement, {
                      className: 'h-4 w-4 sm:h-5 sm:w-5'
                    })}
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs sm:text-sm font-medium truncate">{section.title}</div>
                    <div className="text-xs text-gray-500 mt-1 hidden lg:block">{section.description}</div>
                  </div>
                </button>
              ))}
            </div>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-auto">
          <div className="p-3 sm:p-4 lg:p-6">
            <div className="max-w-4xl mx-auto">
              {renderContent()}
              
              {/* Save Button */}
              {(['general', 'security', 'email'].includes(activeSection)) && (
                <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-600">
                      <Check className="h-4 w-4 text-green-500" />
                      <span>All changes are automatically saved</span>
                    </div>
                    <button
                      onClick={() => handleSaveSettings(activeSection)}
                      disabled={saving}
                      className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white px-4 sm:px-6 py-2 rounded-lg flex items-center justify-center transition-colors"
                    >
                      {saving ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;